/**
 * Facturas (comprobantes de comisión) — `GET /payments/admin/invoices`.
 *
 * La plataforma factura SOLO lo suyo: al comprador su comisión de la app + el
 * envío; al vendedor su comisión por venta. Se emiten con Stripe Invoicing,
 * pagadas fuera de banda (el dinero ya se cobró). No son CFDI (los campos
 * cfdi* se llenarán cuando se conecte un PAC).
 */

/** Parte a la que se factura. */
export type InvoiceParty = 'buyer' | 'seller';

export const INVOICE_PARTY_LABELS: Record<InvoiceParty, string> = {
  buyer: 'Comprador',
  seller: 'Vendedor',
};

/** Estados: emitida → enviada por correo → (anulada permite re-emitir). */
export const INVOICE_STATUS_LABELS: Record<string, string> = {
  issued: 'Emitida',
  sent: 'Enviada',
  void: 'Anulada',
};

/** Concepto facturado (montos IVA incluido; el desglose va en la factura). */
export interface InvoiceConcept {
  key?: string;
  description?: string;
  amount?: number;
  amountCents?: number;
}

/** Factura emitida. */
export interface Invoice {
  id: number;
  paymentId?: number;
  party?: InvoiceParty;
  partyLabel?: string;
  userId?: number;
  recipientName?: string | null;
  recipientEmail?: string | null;
  recipientRfc?: string | null;
  recipientBusinessName?: string | null;
  concepts?: InvoiceConcept[];
  subtotal?: number;
  subtotalCents?: number;
  tax?: number;
  taxCents?: number;
  taxPercent?: number | null;
  total?: number;
  totalCents?: number;
  currency?: string;
  status?: string;
  provider?: string;
  stripeInvoiceId?: string | null;
  stripeInvoiceNumber?: string | null;
  hostedInvoiceUrl?: string | null;
  invoicePdfUrl?: string | null;
  cfdiUuid?: string | null;
  sentAt?: string | null;
  voidedAt?: string | null;
  createdAt?: string;
  [key: string]: unknown;
}

/** Lado (comprador o vendedor) de la previsualización de facturas de un pago. */
export interface InvoicePreviewSide {
  /** true = se puede emitir (hay conceptos y no existe factura vigente). */
  available: boolean;
  /** Por qué no hay nada que facturar (cuando aplica). */
  reason?: string;
  recipient?: {
    userId?: number;
    name?: string | null;
    email?: string | null;
    rfc?: string | null;
    businessName?: string | null;
    taxRegime?: string | null;
  };
  concepts?: InvoiceConcept[];
  subtotal?: number;
  tax?: number;
  taxPercent?: number;
  total?: number;
  /** Factura vigente ya emitida para esta parte, si existe. */
  existingInvoice?: {
    id: number;
    status?: string;
    stripeInvoiceNumber?: string | null;
    hostedInvoiceUrl?: string | null;
    invoicePdfUrl?: string | null;
    totalCents?: number;
    createdAt?: string;
  } | null;
}

/** `GET /payments/admin/invoices/preview?paymentId=`. */
export interface InvoicePreview {
  payment: {
    id: number;
    status?: string;
    currency?: string;
    partTitle?: string | null;
    paidAt?: string;
    /** Solo se facturan pagos exitosos. */
    invoiceable?: boolean;
  };
  buyer: InvoicePreviewSide;
  seller: InvoicePreviewSide;
}

/** Resultado por parte de `POST /payments/admin/invoices` (party: both). */
export interface InvoiceCreateResult {
  buyer?: { ok: boolean; message?: string; invoice?: Invoice; emailSent?: boolean };
  seller?: { ok: boolean; message?: string; invoice?: Invoice; emailSent?: boolean };
}
